import React from 'react';
import { Order } from '@hero/schema';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  RouteArrowDisplay,
} from '@hero/web-components';
import { LocationIcon, RefreshIcon } from '@hero/web-icons';
import { useTranslation } from 'react-i18next';
import { UseOrderAgainConfig, useOrderDestination, useOrderDrawerStore } from '@hero/hooks-shared';
import { OrderAgainManager } from '../../OrderAgain';
import { CellAction } from '../../../../app/Table/CellAction';

export interface ActionsCellProps {
  order: Order;
  hasDetailDrawer?: boolean;
  orderAgainConfig?: UseOrderAgainConfig;
  displayForUser?: 'customer' | 'carrier';
}

/**
 * ActionsCell - Renders the actions menu (view + reorder buttons).
 * Opens the drawer via `useOrderDrawerStore` — no more per-row Sheet.
 */
export const ActionsCell: React.FC<ActionsCellProps> = ({
  order,
  hasDetailDrawer = false,
  orderAgainConfig,
  displayForUser = 'customer',
}) => {
  const { t } = useTranslation('m-order');
  const { firstPickup, lastDropoff } = useOrderDestination(order.destinations);
  const openDrawer = useOrderDrawerStore(s => s.open);

  if (!hasDetailDrawer && !orderAgainConfig) {
    return null;
  }

  return (
    <TooltipProvider>
      <div className="flex items-center gap-2 justify-start" onClick={e => e.stopPropagation()}>
        {orderAgainConfig && (
          <OrderAgainManager
            order={order}
            config={orderAgainConfig}
            displayForUser={displayForUser}
            showButton={true}
            trigger={
              <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="p-1.5 hover:bg-gray-100 hover:scale-110 active:scale-95 rounded transition-all duration-200 cursor-pointer">
                    <RefreshIcon className="w-4 h-4 text-gray-600 hover:text-gray-900 transition-colors" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="z-50">
                  <div className="flex flex-col gap-1 items-center">
                    <div className="flex items-center gap-2">
                      <LocationIcon className="size-3" />
                      <RouteArrowDisplay
                        origin={firstPickup}
                        destinations={[lastDropoff]}
                        size="small"
                      />
                    </div>
                    <span className="text-xs text-muted-foreground">{t('order_again')}</span>
                  </div>
                </TooltipContent>
              </Tooltip>
            }
          />
        )}

        {hasDetailDrawer && (
          <CellAction type="view" onClick={() => openDrawer(String(order._id))} />
        )}
      </div>
    </TooltipProvider>
  );
};
